// Libs
import moment from 'moment';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';
// Others
import { DEFAULT_VIEW_MODE, SCHEDULE_MODE_FULL } from '~/utils/constants/common';
import { ISchedule, IScheduleTimeLine } from '~/utils/interface/schedule';
import { CalendarViewMode, ScheduleMode } from '~/utils/type/common';
import { DateFormatEnum } from '~/utils/enum/common';
import { RootState } from '~/utils/redux/store';
import {
  createSchedule,
  getScheduleDetail,
  getScheduleList,
  deleteSchedule,
  updateSchedule,
  getScheduleDashboardList,
} from './scheduleThunk';

export interface ScheduleState {
  events: ISchedule[];
  timeLineEvents: IScheduleTimeLine[];
  scheduleMode: ScheduleMode;
  calendarViewMode: CalendarViewMode;
  selectedEventId: string | undefined;
  openAlertDelete: boolean;
  needRefreshData: boolean;
  currentTime: string;
  isRefreshScheduleList: boolean;
}

const initialState: ScheduleState = {
  events: [],
  timeLineEvents: [],
  scheduleMode: SCHEDULE_MODE_FULL,
  calendarViewMode: DEFAULT_VIEW_MODE,
  currentTime: moment().format(DateFormatEnum.YYYY_MM_DD),
  selectedEventId: undefined,
  openAlertDelete: false,
  needRefreshData: false,
  isRefreshScheduleList: false,
};

const scheduleSlice = createSlice({
  name: 'schedule',
  initialState,
  reducers: {
    goToday(state) {
      state.currentTime = moment().format(DateFormatEnum.YYYY_MM_DD);
    },
    goPreviousTime(state) {
      const previousTime = moment(state.currentTime).subtract(1, state.calendarViewMode);
      state.currentTime = previousTime.format(DateFormatEnum.YYYY_MM_DD);
    },
    goNextTime(state) {
      const nextTime = moment(state.currentTime).add(1, state.calendarViewMode);
      state.currentTime = nextTime.format(DateFormatEnum.YYYY_MM_DD);
    },
    changeScheduleMode(state, action: PayloadAction<ScheduleMode>) {
      state.scheduleMode = action.payload;
    },
    changeCalendarViewMode(state, action: PayloadAction<CalendarViewMode>) {
      state.currentTime = moment().format(DateFormatEnum.YYYY_MM_DD);
      state.calendarViewMode = action.payload;
    },
    changeOpenAlertDelete(state, action: PayloadAction<boolean>) {
      state.openAlertDelete = action.payload;
    },
    changeSelectedEventId(state, action: PayloadAction<string>) {
      state.selectedEventId = action.payload;
    },
    changeNeedRefreshData(state, action: PayloadAction<boolean>) {
      state.needRefreshData = action.payload;
    },
    changeEvents(state, action: PayloadAction<ISchedule[]>) {
      state.events = action.payload;
    },
    changeTimeLineEvents(state, action: PayloadAction<IScheduleTimeLine[]>) {
      state.timeLineEvents = action.payload;
    },
    setRefreshScheduleList(state, action: PayloadAction<boolean>) {
      state.isRefreshScheduleList = action.payload;
    },
  },
  extraReducers(builder) {
    // POST: Create schedule
    builder
      .addCase(createSchedule.pending, (state) => {})
      .addCase(createSchedule.fulfilled, (state) => {})
      .addCase(createSchedule.rejected, (state) => {});

    // GET: Get schedule list
    builder
      .addCase(getScheduleList.pending, (state) => {})
      .addCase(getScheduleList.fulfilled, (state, action) => {
        if (state.calendarViewMode === 'month') {
          state.events = action.payload.data as ISchedule[];
        } else if (state.calendarViewMode === 'week' || state.calendarViewMode === 'day') {
          state.timeLineEvents = action.payload.data as IScheduleTimeLine[];
        }
        state.needRefreshData = false;
      })
      .addCase(getScheduleList.rejected, (state) => {});

    // GET: Delete schedule
    builder
      .addCase(getScheduleDashboardList.pending, (state) => {})
      .addCase(getScheduleDashboardList.fulfilled, (state) => {})
      .addCase(getScheduleDashboardList.rejected, (state) => {});

    // DELETE: Delete schedule
    builder
      .addCase(deleteSchedule.pending, (state) => {})
      .addCase(deleteSchedule.fulfilled, (state) => {})
      .addCase(deleteSchedule.rejected, (state) => {});

    // GET: Get schedule detail
    builder
      .addCase(getScheduleDetail.pending, (state) => {})
      .addCase(getScheduleDetail.fulfilled, (state) => {})
      .addCase(getScheduleDetail.rejected, (state) => {});

    // PUT: Create schedule
    builder
      .addCase(updateSchedule.pending, (state) => {})
      .addCase(updateSchedule.fulfilled, (state) => {})
      .addCase(updateSchedule.rejected, (state) => {});
  },
});

// Action
export const scheduleActions = scheduleSlice.actions;

// Selectors
export const selectIsRefreshScheduleList = (state: RootState) => state.schedule.isRefreshScheduleList;

// Reducer
const scheduleReducer = scheduleSlice.reducer;
export default scheduleReducer;
